import React, { useState, useMemo, useCallback } from "react";
import "./PaginaRevision.css";

function PaginaRevision({
  examen,
  respuestas,
  marcadas,
  preguntasOrden,
  onIrAPregunta,
  disabled,
}) {
  const totalPreguntas = preguntasOrden.length;

  // Estados para los filtros
  const [filtroCategoria, setFiltroCategoria] = useState("all");
  const [filtroEstado, setFiltroEstado] = useState("all");
  const [busquedaTexto, setBusquedaTexto] = useState("");

  // Función para determinar el estado de una pregunta
  const getEstadoPregunta = useCallback(
    (preguntaId) => {
      const respuesta = respuestas[preguntaId];
      const estaMarcada = marcadas[preguntaId] === true;

      // Verificar si tiene respuesta válida
      // Para multiple_choice: número o array de ids (preguntas con varias correctas)
      // Para hotspot/drag_drop: objeto (dict) no vacío
      let tieneRespuesta = false;
      if (respuesta !== undefined && respuesta !== null) {
        if (typeof respuesta === "number") {
          tieneRespuesta = true;
        } else if (Array.isArray(respuesta)) {
          tieneRespuesta = respuesta.length > 0;
        } else if (typeof respuesta === "object") {
          tieneRespuesta = Object.keys(respuesta).length > 0;
        }
      }

      if (estaMarcada) {
        return "marcada";
      } else if (tieneRespuesta) {
        return "respondida";
      } else {
        return "sin_responder";
      }
    },
    [respuestas, marcadas]
  );

  // Contar preguntas por estado
  const contarPorEstado = () => {
    let respondidas = 0;
    let sinResponder = 0;
    let marcadasCount = 0;

    preguntasOrden.forEach((preguntaId) => {
      const estado = getEstadoPregunta(preguntaId);
      if (estado === "respondida") {
        respondidas++;
      } else if (estado === "marcada") {
        marcadasCount++;
      } else {
        sinResponder++;
      }
    });

    return { respondidas, sinResponder, marcadas: marcadasCount };
  };

  const conteos = contarPorEstado();

  // Obtener todas las categorías únicas del examen
  const categorias = useMemo(() => {
    const catsSet = new Set();
    examen.preguntas.forEach((p) => {
      const catNombre = p.categoria?.nombre || "General";
      catsSet.add(catNombre);
    });
    return Array.from(catsSet).sort();
  }, [examen]);

  // Función de filtrado
  const preguntasFiltradas = useMemo(() => {
    return preguntasOrden.filter((preguntaId) => {
      const pregunta = examen.preguntas.find(
        (p) => Number(p.id) === Number(preguntaId)
      );
      if (!pregunta) return false;

      // Filtro por categoría
      const categoriaNombre = pregunta.categoria?.nombre || "General";
      if (filtroCategoria !== "all" && categoriaNombre !== filtroCategoria) {
        return false;
      }

      // Filtro por estado
      const estado = getEstadoPregunta(preguntaId);
      if (filtroEstado !== "all") {
        if (filtroEstado === "answered" && estado !== "respondida")
          return false;
        if (filtroEstado === "unanswered" && estado !== "sin_responder")
          return false;
        if (filtroEstado === "marked" && estado !== "marcada") return false;
      }

      // Filtro por texto de búsqueda
      if (busquedaTexto.trim() !== "") {
        const textoBusqueda = busquedaTexto.toLowerCase();
        const textoPregunta = pregunta.texto.toLowerCase();
        const categoriaBusqueda = categoriaNombre.toLowerCase();
        if (
          !textoPregunta.includes(textoBusqueda) &&
          !categoriaBusqueda.includes(textoBusqueda)
        ) {
          return false;
        }
      }

      return true;
    });
  }, [
    preguntasOrden,
    examen,
    filtroCategoria,
    filtroEstado,
    busquedaTexto,
    getEstadoPregunta,
  ]);

  // Limpiar filtros
  const limpiarFiltros = () => {
    setFiltroCategoria("all");
    setFiltroEstado("all");
    setBusquedaTexto("");
  };

  return (
    <div className="pagina-revision-container">
      <div className="pagina-revision-header">
        <h2>Exam Review</h2>
        <div className="pagina-revision-resumen">
          <div className="resumen-item respondida">
            <span className="resumen-indicador"></span>
            <span className="resumen-texto">
              Answered: <strong>{conteos.respondidas}</strong>
            </span>
          </div>
          <div className="resumen-item sin-responder">
            <span className="resumen-indicador"></span>
            <span className="resumen-texto">
              Unanswered: <strong>{conteos.sinResponder}</strong>
            </span>
          </div>
          <div className="resumen-item marcada">
            <span className="resumen-indicador"></span>
            <span className="resumen-texto">
              Marked for review: <strong>{conteos.marcadas}</strong>
            </span>
          </div>
          <div className="resumen-item total">
            <span className="resumen-texto">
              Total: <strong>{totalPreguntas}</strong>
            </span>
          </div>
        </div>
      </div>

      <div className="pagina-revision-instrucciones">
        <p>
          Review all exam questions. You can click on any question to return to
          it.
        </p>
      </div>

      {/* Sección de Filtros */}
      <div className="pagina-revision-filtros">
        <div className="filtros-row">
          {/* Barra de búsqueda */}
          <div className="filtro-busqueda">
            <div className="input-busqueda-wrapper">
              <img
                src="/icons/filtrar.png"
                alt="Search"
                className="icono-filtro"
              />
              <input
                type="text"
                placeholder="Search by text..."
                value={busquedaTexto}
                onChange={(e) => setBusquedaTexto(e.target.value)}
                className="input-busqueda"
              />
            </div>
          </div>

          {/* Filtro por categoría */}
          <div className="filtro-select">
            <label htmlFor="filtro-categoria">
              <img
                src="/icons/categoria.png"
                alt="Category"
                className="icono-label"
              />
              Category:
            </label>
            <select
              id="filtro-categoria"
              value={filtroCategoria}
              onChange={(e) => setFiltroCategoria(e.target.value)}
              className="select-filtro"
            >
              <option value="todas">All categories</option>
              {categorias.map((cat) => (
                <option key={cat} value={cat}>
                  {cat}
                </option>
              ))}
            </select>
          </div>

          {/* Filtro por estado */}
          <div className="filtro-select">
            <label htmlFor="filtro-estado">
              <img
                src="/icons/lista-de-verificacion.png"
                alt="Status"
                className="icono-label"
              />
              Status:
            </label>
            <select
              id="filtro-estado"
              value={filtroEstado}
              onChange={(e) => setFiltroEstado(e.target.value)}
              className="select-filtro"
            >
              <option value="all">All statuses</option>
              <option value="answered">Answered</option>
              <option value="unanswered">Unanswered</option>
              <option value="marked">Marked for review</option>
            </select>
          </div>

          {/* Botón limpiar filtros */}
          {(filtroCategoria !== "all" ||
            filtroEstado !== "all" ||
            busquedaTexto.trim() !== "") && (
            <button
              onClick={limpiarFiltros}
              className="btn-limpiar-filtros"
              title="Clear all filters"
            >
              Clear filters
            </button>
          )}
        </div>

        {/* Contador de resultados filtrados */}
        <div className="filtros-contador">
          <span>
            Showing <strong>{preguntasFiltradas.length}</strong> of{" "}
            <strong>{totalPreguntas}</strong> questions
          </span>
        </div>
      </div>

      <div className="pagina-revision-grid">
        {preguntasFiltradas.length === 0 ? (
          <div className="sin-resultados">
            <p>No questions found with the applied filters.</p>
            <button onClick={limpiarFiltros} className="btn-limpiar-filtros">
              Clear filters
            </button>
          </div>
        ) : (
          preguntasFiltradas.map((preguntaId) => {
            // Convertir ambos a número para comparación segura
            const pregunta = examen.preguntas.find(
              (p) => Number(p.id) === Number(preguntaId)
            );
            if (!pregunta) return null;

            // Encontrar el índice original en preguntasOrden para el número de pregunta
            const indexOriginal = preguntasOrden.indexOf(preguntaId);
            const estado = getEstadoPregunta(preguntaId);
            const numeroPregunta = indexOriginal + 1;

            return (
              <div
                key={preguntaId}
                className={`pregunta-revision-card pregunta-${estado}`}
                onClick={() => !disabled && onIrAPregunta(indexOriginal)}
              >
                <div className="pregunta-revision-numero">{numeroPregunta}</div>
                <div className="pregunta-revision-contenido">
                  <div className="pregunta-revision-texto">
                    {pregunta.texto.replace(/\n/g, " ").substring(0, 100)}
                    {pregunta.texto.replace(/\n/g, " ").length > 100 && "..."}
                  </div>
                  <div className="pregunta-revision-categoria">
                    {pregunta.categoria?.nombre || "General"}
                  </div>
                  {estado === "marcada" && (
                    <div className="pregunta-revision-marcada-badge">
                      Marked to review
                    </div>
                  )}
                </div>
                <div className="pregunta-revision-indicador">
                  {estado === "respondida" && (
                    <span className="indicador-respondida">✓</span>
                  )}
                  {(estado === "sin_responder" || estado === "marcada") && (
                    <span className="indicador-pendiente">!</span>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

export default PaginaRevision;
